import fs from 'node:fs';
import { StringDecoder } from 'node:string_decoder';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { LINE_CHUNK_SIZE, READ_RANGE_YIELD_EVERY_BYTES, type ReadFileResult } from './types.js';
import {
  createLineZeroStreamState,
  finishLineZeroTail,
  lineContentBeforeNewline,
  shouldTrackLineZero,
  tryFinishLineZeroOnCompleteLine,
  tryFinishLineZeroPartial,
} from './read-file-range-line-zero.js';

/**
 * Read a line range using streaming I/O so we never load the entire file into memory.
 * When line 0 is longer than lineChunkSize, returns only the first chunk and lineOffset for the next chunk.
 * lineOffsetIn: when provided, we're continuing to read line 0 from this character offset (skip that many chars of line 0).
 */
export async function readFileRangeAsync(
  filePath: string,
  startLine: number,
  endLine: number,
  lineOffsetIn?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
  fileSizeIn?: number,
): Promise<ReadFileResult> {
  const fileSize = fileSizeIn ?? (await fs.promises.stat(filePath)).size;
  const fd = await fs.promises.open(filePath, 'r');
  const bufSize = 64 * 1024;
  const buf = Buffer.alloc(bufSize);
  const decoder = new StringDecoder('utf8');
  let currentLine = 0;
  const resultLines: string[] = [];
  let partial = '';
  let done = false;
  const trackLineZero = shouldTrackLineZero(startLine, lineOffsetIn);
  const lineZeroState = createLineZeroStreamState(lineOffsetIn);
  let bytesSinceYield = 0;

  try {
    let position = 0;
    while (!done) {
      const { bytesRead } = await fd.read(buf, 0, bufSize, position);
      if (bytesRead === 0) break;
      position += bytesRead;
      bytesSinceYield += bytesRead;
      if (bytesSinceYield >= READ_RANGE_YIELD_EVERY_BYTES) {
        await yieldToEventLoop();
        bytesSinceYield = 0;
      }

      const text = partial + decoder.write(buf.subarray(0, bytesRead));
      partial = '';
      let lineStart = 0;
      for (let i = 0; i < text.length; i++) {
        if (text[i] !== '\n') continue;

        const lineContent = lineContentBeforeNewline(text, lineStart, i);
        if (currentLine === 0 && trackLineZero) {
          const lineZeroResult = tryFinishLineZeroOnCompleteLine(
            lineZeroState,
            lineContent,
            resultLines,
            fileSize,
            lineChunkSize,
            lineOffsetIn,
            endLine,
            currentLine,
          );
          if (lineZeroResult === 'done') {
            done = true;
            break;
          }
          if (lineZeroResult !== 'continue') return lineZeroResult;
          currentLine++;
          lineStart = i + 1;
          if (currentLine > endLine) {
            done = true;
            break;
          }
          continue;
        }

        if (currentLine >= startLine && currentLine <= endLine) {
          resultLines.push(lineContent);
        }
        currentLine++;
        lineStart = i + 1;
        if (currentLine > endLine) {
          done = true;
          break;
        }
      }

      if (done) continue;

      const lineContent = text.slice(lineStart);
      if (currentLine === 0 && trackLineZero) {
        const partialResult = tryFinishLineZeroPartial(
          lineZeroState,
          lineContent,
          resultLines,
          fileSize,
          lineChunkSize,
          lineOffsetIn,
        );
        if (partialResult != null) return partialResult;
      } else {
        partial = lineContent;
      }
    }

    if (!done) {
      const tail = partial + decoder.end();
      if (currentLine === 0 && trackLineZero) {
        const tailResult = finishLineZeroTail(
          lineZeroState,
          tail,
          resultLines,
          fileSize,
          lineChunkSize,
          lineOffsetIn,
        );
        if (tailResult != null) return tailResult;
      } else if (tail.length > 0 && currentLine >= startLine && currentLine <= endLine) {
        resultLines.push(tail.endsWith('\r') ? tail.slice(0, -1) : tail);
      }
    }

    return { content: resultLines.join('\n'), size: fileSize };
  } finally {
    await fd.close();
  }
}
